import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Upload, X, Check } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface LogoUploadProps {
  value: string;
  onChange: (url: string) => void;
  tenantName: string;
}

export const LogoUpload: React.FC<LogoUploadProps> = ({ value, onChange, tenantName }) => {
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(value);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const { toast } = useToast();

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Invalid File',
        description: 'Please select an image file (PNG, JPG, etc.)',
        variant: 'destructive',
      });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: 'File Too Large',
        description: 'Please select an image smaller than 5MB',
        variant: 'destructive',
      });
      return;
    }

    setUploading(true);
    try {
      await processLogoWithBranding(file);
    } catch (error) {
      toast({
        title: 'Upload Failed',
        description: 'Failed to process logo. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setUploading(false);
    }
  };

  const processLogoWithBranding = async (file: File): Promise<void> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const canvas = canvasRef.current;
      if (!canvas) {
        reject(new Error('Canvas not available'));
        return;
      }

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Canvas context not available'));
        return;
      }

      img.onload = async () => {
        try {
          // Set canvas dimensions
          const maxWidth = 400;
          const maxHeight = 200;
          let { width, height } = img;

          // Maintain aspect ratio
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
          if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }

          // Set canvas dimensions (no extra space needed)
          canvas.width = width;
          canvas.height = height;

          // Clear canvas
          ctx.fillStyle = '#FFFFFF';
          ctx.fillRect(0, 0, canvas.width, canvas.height);

          // Draw the logo (clean, without branding text)
          ctx.drawImage(img, 0, 0, width, height);

          // Convert to blob and upload to Supabase
          canvas.toBlob(async (blob) => {
            if (blob) {
              try {
                // Upload to Supabase storage (using public insurer-logos bucket)
                const fileName = `tenant-logo-${Date.now()}.png`;
                const { data, error } = await supabase.storage
                  .from('insurer-logos')
                  .upload(fileName, blob, {
                    contentType: 'image/png',
                    upsert: true
                  });

                if (error) {
                  throw error;
                }

                // Get public URL from public bucket
                const { data: { publicUrl } } = supabase.storage
                  .from('insurer-logos')
                  .getPublicUrl(fileName);

                console.log('Logo uploaded successfully:', publicUrl);
                setPreviewUrl(publicUrl);
                onChange(publicUrl);

                toast({
                  title: 'Logo Uploaded',
                  description: 'Logo has been uploaded successfully',
                });
                resolve();
              } catch (uploadError) {
                console.error('Upload error:', uploadError);
                reject(uploadError);
              }
            } else {
              reject(new Error('Failed to create blob'));
            }
          }, 'image/png');
        } catch (error) {
          reject(error);
        }
      };

      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = URL.createObjectURL(file);
    });
  };

  const handleUrlChange = (url: string) => {
    setPreviewUrl(url);
    onChange(url);

    // No branding validation needed - clean logos are now allowed
  };

  const clearLogo = () => {
    setPreviewUrl('');
    onChange('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Logo Upload *</Label>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="flex-1"
          >
            <Upload className="h-4 w-4 mr-2" />
            {uploading ? 'Processing...' : 'Upload Logo'}
          </Button>
          {previewUrl && (
            <Button
              type="button"
              variant="outline"
              size="icon"
              onClick={clearLogo}
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="logo-url">Or Enter Logo URL *</Label>
        <Input
          id="logo-url"
          placeholder="https://example.com/logo.png"
          value={value}
          onChange={(e) => handleUrlChange(e.target.value)}
        />
      </div>

      {previewUrl && (
        <div className="space-y-2">
          <Label>Logo Preview</Label>
          <div className="border rounded-lg p-4 bg-gray-50">
            <img
              src={previewUrl}
              alt="Logo preview"
              className="max-w-full max-h-32 object-contain mx-auto"
            />
          </div>
          <div className="flex items-center gap-2 text-sm text-green-600">
            <Check className="h-4 w-4" />
            Logo uploaded successfully
          </div>
        </div>
      )}

      <div className="text-xs text-muted-foreground space-y-1">
        <p>• Supported formats: PNG, JPG, GIF (max 5MB)</p>
        <p>• Recommended size: 400x200px or smaller</p>
        <p>• Upload clean logos - YallaCompare branding appears separately in banners</p>
      </div>

      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
};
